import React from 'react';

import AgentDeliveryMethodFormFields from './AgentDeliveryMethodFormFields.tsx';
import AgentDetailsFormFields from './AgentDetailsFormFields.tsx';
import AgentKnowledgeFormFields from './AgentKnowledgeFormFields.tsx';
import AgentSkillsFormFields from './AgentSkillsFormFields.tsx';
import AgentSummaryFormFields from './AgentSummaryFormFields.tsx';
import AgentToolProvidersFormFields from './AgentToolProvidersFormFields.tsx';
import AgentTriggersFormFields from './AgentTriggersFormFields.tsx';
import { InstructionSection } from '../knowledge/InstructionSections.tsx';

export interface SharePointSelectedItem {
    id: string;
    path: string[];
}

export interface SourceSyncInfo {
    syncStatus?: 'synced' | 'failed' | 'pending';
    syncError?: string | null;
    lastSyncedAt?: string | null;
}

export interface SharePointToolProviderConfig extends SourceSyncInfo {
    integrationId: string;
    siteId: string;
    driveId: string;
    selectedItems: SharePointSelectedItem[];
    // display names stored with the ids so project members without browsing rights can read the setup
    integrationName?: string | null;
    siteName?: string | null;
    driveName?: string | null;
}

export const SHAREPOINT_TOOL_PROVIDER_DEFAULT: SharePointToolProviderConfig = {
    integrationId: '',
    siteId: '',
    driveId: '',
    selectedItems: [],
};

export interface SftpKnowledgeSourceConfig extends SourceSyncInfo {
    sftpConfigId: string;
    path: string;
}

export const SFTP_KNOWLEDGE_SOURCE_DEFAULT: SftpKnowledgeSourceConfig = {
    sftpConfigId: '',
    path: '/',
};

export const MAX_KNOWLEDGE_URLS = 10;
export const MAX_KNOWLEDGE_URL_LENGTH = 255;

export const isValidKnowledgeUrl = (url: string) => {
    const trimmed = url.trim();
    return (
        trimmed.startsWith('https://') &&
        trimmed.length > 'https://'.length &&
        trimmed.length <= MAX_KNOWLEDGE_URL_LENGTH
    );
};

export interface DefaultChannelsConfig {
    orderChannelId: string;
    orderConfirmationChannelId: string;
    rfqChannelId: string;
    invoiceChannelId: string;
    deliveryNoteChannelId: string;
}

export const AGENT_FORM_DEFAULT_VALUES = {
    name: '',
    description: '',
    instructions: [] as InstructionSection[],
    erpToolProvider: null as { key: string } | null,
    masterDataToolProvider: null as { channelId: string; documentType: string } | null,
    sharepointToolProvider: null as SharePointToolProviderConfig | null,
    sftpKnowledgeSource: null as SftpKnowledgeSourceConfig | null,
    knowledgeUrls: [] as string[],
    orderHistoryToolProvider: null as { enabled: boolean } | null,
    defaultChannels: null as DefaultChannelsConfig | null,
    attachmentsEnabled: false,
    skillNames: [],
    triggerIds: [],
    knowledgeBaseIds: [],
    files: [],
    deliveryMethod: 'auto',
    outputInstructions: [] as InstructionSection[],
    teamId: undefined,
};

export interface AgentFormData {
    name: string;
    description: string;
    instructions: InstructionSection[];
    erpToolProvider: { key: string } | null;
    masterDataToolProvider: { channelId: string; documentType: string } | null;
    sharepointToolProvider: SharePointToolProviderConfig | null;
    sftpKnowledgeSource: SftpKnowledgeSourceConfig | null;
    knowledgeUrls: string[];
    orderHistoryToolProvider: { enabled: boolean } | null;
    defaultChannels: DefaultChannelsConfig | null;
    attachmentsEnabled: boolean;
    skillNames: string[];
    triggerIds: string[];
    knowledgeBaseIds: string[];
    files: (File | any)[];
    deliveryMethod: string;
    outputInstructions: InstructionSection[];
    teamId?: string;
}

export interface AgentConfigStep {
    title: string;
    description: string;
    component: React.ComponentType<{
        form: any;
        agent?: any;
        availableToolProviders?: any[];
        availableSkills?: any[];
        availableToolNames?: string[];
        availableSmartInboxTriggers?: any[];
        teamOptions?: any[];
    }>;
    isValid?: (form: any) => boolean;
    isSkippable?: boolean;
}

export const AGENT_CONFIG_STEPS: Record<string, AgentConfigStep> = {
    details: {
        title: `form.detailsSectionTitle`,
        description: `form.detailsSectionDescription`,
        component: ({ form, teamOptions }) => <AgentDetailsFormFields form={form} teamOptions={teamOptions} />,
        isValid: (form) => form.watch('name').trim().length > 0,
    },

    skills: {
        title: `form.skillsSectionTitle`,
        description: `form.skillsSectionDescription`,
        component: ({ form, availableSkills, availableToolNames }) => (
            <AgentSkillsFormFields
                form={form}
                availableSkills={availableSkills}
                availableToolNames={availableToolNames}
            />
        ),
        isValid: (form) => form.watch('skillNames').length > 0,
    },
    toolProviders: {
        title: `form.toolProvidersSectionTitle`,
        description: `form.toolProvidersSectionDescription`,
        component: ({ form, availableToolProviders }) => (
            <AgentToolProvidersFormFields form={form} availableToolProviders={availableToolProviders} />
        ),
        isValid: (form) => {
            const md = form.watch('masterDataToolProvider');
            const sp = form.watch('sharepointToolProvider');
            const sftp = form.watch('sftpKnowledgeSource');
            const mdValid = !md || Boolean(md.channelId);
            const spValid = !sp || (Boolean(sp.integrationId) && Boolean(sp.driveId) && sp.selectedItems.length > 0);
            const sftpValid = !sftp || (Boolean(sftp.sftpConfigId) && Boolean(sftp.path));
            return mdValid && spValid && sftpValid;
        },
    },
    knowledge: {
        title: `form.knowledgeSectionTitle`,
        description: `form.knowledgeSectionDescription`,
        component: ({ form, agent }) => <AgentKnowledgeFormFields form={form} agent={agent} />,
        isValid: (form) =>
            (form.watch('knowledgeUrls') || []).every((url: string) => !url.trim() || isValidKnowledgeUrl(url)),
        isSkippable: true,
    },
    delivery: {
        title: `form.deliverySectionTitle`,
        description: `form.deliverySectionDescription`,
        component: ({ form }) => <AgentDeliveryMethodFormFields form={form} />,
        isValid: (form) => form.watch('deliveryMethod')?.length > 0,
    },
    triggers: {
        title: `form.triggersSectionTitle`,
        description: `form.triggersSectionDescription`,
        component: ({ form, agent, availableSmartInboxTriggers }) => (
            <AgentTriggersFormFields
                form={form}
                agent={agent}
                availableSmartInboxTriggers={availableSmartInboxTriggers}
            />
        ),
        isSkippable: true,
    },
    summary: {
        title: `form.summarySectionTitle`,
        description: `form.summarySectionDescription`,
        component: ({ form }) => <AgentSummaryFormFields form={form} />,
    },
};
